import { useEffect, useRef } from 'react';

const useIframeAutoResize = () => {
  const lastSentHeight = useRef(null);
  const debounceTimeout = useRef(null);
  const hasInitialized = useRef(false);
  // Track if any pending call is a resize event so it doesn't get downgraded
  const pendingIsResize = useRef(false);

  useEffect(() => {
    if (window.self === window.top) return;

    const sendHeightToParent = (forceResize = false, isResizeEvent = false) => {
      // If there's already a pending call, preserve the "most important" flags
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
        // If the pending call was a resize event, keep that flag
        if (pendingIsResize.current) {
          isResizeEvent = true;
        }
      }

      pendingIsResize.current = isResizeEvent;

      // Use longer debounce for resize events
      const debounceTime = isResizeEvent ? 200 : forceResize ? 100 : 50;

      debounceTimeout.current = setTimeout(() => {
        debounceTimeout.current = null;
        pendingIsResize.current = false;

        const bodyHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight);

        // Check if there's overflow (scrollbar is showing)
        const hasOverflow = document.documentElement.scrollHeight > document.documentElement.clientHeight;

        // On initial load OR resize event, only send height if there's a scrollbar
        if ((!hasInitialized.current || isResizeEvent) && !hasOverflow) {
          if (!hasInitialized.current) {
            hasInitialized.current = true;
          }
          return;
        }

        hasInitialized.current = true;

        const height = bodyHeight + 20;

        if (height > 2000) {
          return;
        }

        if (lastSentHeight.current !== null && Math.abs(lastSentHeight.current - height) < 5) {
          return;
        }

        // For non-resize events after initialization:
        // Only resize if there's a scrollbar (growing) OR current height > 1000px (allow shrinking)
        if (!isResizeEvent && !hasOverflow) {
          const currentHeight = lastSentHeight.current || 0;
          if (currentHeight <= 1000) {
            return;
          }
        }

        lastSentHeight.current = height;

        window.parent.postMessage(
          {
            type: 'heavyset-iframe-height-change',
            height: height
          },
          '*'
        );
      }, debounceTime);
    };

    const handleMessage = (event) => {
      if (event.data?.type === 'heavyset-recalculate-height') {
        // Parent has reset the iframe height externally, so clear our
        // cached value — otherwise we'll skip sending the same height again.
        lastSentHeight.current = null;

        // Check if this is a resize event (parent cleared height)
        const isResizeEvent = event.data?.isResize === true;
        sendHeightToParent(true, isResizeEvent);
      }
    };
    window.addEventListener('message', handleMessage);

    const initialTimeout = setTimeout(() => sendHeightToParent(false, false), 100);

    let contentResizeObserver;
    let pollInterval;

    // Window resize events from within the iframe
    const handleWindowResize = () => sendHeightToParent(true, false);
    window.addEventListener('resize', handleWindowResize);

    if ('ResizeObserver' in window) {
      contentResizeObserver = new ResizeObserver(() => {
        if (hasInitialized.current) {
          sendHeightToParent(true, false);
        }
      });
      contentResizeObserver.observe(document.body);
    } else {
      pollInterval = setInterval(() => sendHeightToParent(true, false), 500);
    }

    return () => {
      clearTimeout(initialTimeout);
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('message', handleMessage);
      if (contentResizeObserver) {
        contentResizeObserver.disconnect();
      }
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, []);
};

export default useIframeAutoResize;
